import http from './http'
import baseURL from './baseURL/baseURL'
import store from '../store'
import utils from './utils'
import { WEB_IDENTITY_TOKEN_KEY } from './webIdentityToken'

const OAUTH_STATE_KEY = 'wxOauthState'
const OAUTH_RETRY_KEY = 'wxOauthRetryCount'
const DEBUG_OPENID_KEY = 'webIdentityDebugOpenId'
let initializingPromise = null

export function isWeChatBrowser() {
  return /micromessenger/i.test(window.navigator.userAgent || '')
}

export function getCleanShareUrl(rawUrl = window.location.href) {
  const url = new URL(rawUrl, window.location.origin)
  url.searchParams.delete('code')
  url.searchParams.delete('state')
  return url.toString()
}

function createOAuthState() {
  const bytes = new Uint8Array(24)
  window.crypto.getRandomValues(bytes)
  return Array.from(bytes).map(item => item.toString(16).padStart(2, '0')).join('')
}

function replaceWithCleanUrl() {
  const cleanUrl = getCleanShareUrl()
  window.history.replaceState(window.history.state, document.title, cleanUrl)
  return cleanUrl
}

function setAuthenticated(data) {
  if (data.token) {
    window.localStorage.setItem(WEB_IDENTITY_TOKEN_KEY, data.token)
  }
  store.commit('webIdentity/SET_AUTHENTICATED', data)
  store.commit('userInfo/SET_USER', data.user || {})
  return { status: 'authenticated', data }
}

function getDebugIdentityConfig() {
  return {
    enabled: process.env.NODE_ENV === 'development' && process.env.VUE_APP_WEB_IDENTITY_DEBUG_ENABLED === 'true',
    openid: (process.env.VUE_APP_WEB_IDENTITY_DEBUG_OPENID || '').trim()
  }
}

function clearDebugIdentity() {
  if (window.localStorage.getItem(DEBUG_OPENID_KEY)) {
    window.localStorage.removeItem(DEBUG_OPENID_KEY)
    window.localStorage.removeItem(WEB_IDENTITY_TOKEN_KEY)
  }
}

async function initializeDebugIdentity(openid) {
  if (!openid) {
    clearDebugIdentity()
    throw new Error('已开启本地微信身份模拟，请配置 VUE_APP_WEB_IDENTITY_DEBUG_OPENID')
  }

  const savedOpenId = window.localStorage.getItem(DEBUG_OPENID_KEY)
  const token = window.localStorage.getItem(WEB_IDENTITY_TOKEN_KEY)
  if (token && savedOpenId === openid) {
    const sessionResult = await http.get(baseURL.weixin.webSession(), 1)
    const sessionUser = sessionResult && sessionResult.data && sessionResult.data.user
    if (sessionResult && sessionResult.code === 200 && sessionUser && sessionUser.openid === openid) {
      return setAuthenticated(sessionResult.data)
    }
  }

  window.localStorage.removeItem(WEB_IDENTITY_TOKEN_KEY)
  window.localStorage.removeItem(DEBUG_OPENID_KEY)
  const result = await http.postParams(baseURL.weixin.debugWebLogin(), { openid }, 1)
  if (!result || result.code !== 200 || !result.data || !result.data.token) {
    throw new Error((result && result.message) || '本地微信身份模拟失败')
  }
  window.localStorage.setItem(DEBUG_OPENID_KEY, openid)
  return setAuthenticated(result.data)
}

async function startAuthorization() {
  const result = await http.get(baseURL.weixin.webConfig(), 1)
  if (!result || result.code !== 200 || !result.data || !result.data.appId) {
    throw new Error((result && result.message) || '获取微信公众号配置失败')
  }
  const state = createOAuthState()
  const redirectUri = getCleanShareUrl()
  window.sessionStorage.setItem(OAUTH_STATE_KEY, state)
  const authUrl = 'https://open.weixin.qq.com/connect/oauth2/authorize' +
    `?appid=${encodeURIComponent(result.data.appId)}` +
    `&redirect_uri=${encodeURIComponent(redirectUri)}` +
    '&response_type=code&scope=snsapi_userinfo' +
    `&state=${encodeURIComponent(state)}#wechat_redirect`
  window.location.replace(authUrl)
  return { status: 'redirecting' }
}

async function retryAuthorization(message) {
  replaceWithCleanUrl()
  window.sessionStorage.removeItem(OAUTH_STATE_KEY)
  const retryCount = Number(window.sessionStorage.getItem(OAUTH_RETRY_KEY) || 0)
  if (retryCount < 1) {
    window.sessionStorage.setItem(OAUTH_RETRY_KEY, String(retryCount + 1))
    return startAuthorization()
  }
  throw new Error(message)
}

async function initialize() {
  store.commit('webIdentity/SET_LOADING')
  utils.removeCookie('user')

  const debugIdentity = getDebugIdentityConfig()
  if (debugIdentity.enabled) {
    return initializeDebugIdentity(debugIdentity.openid)
  }
  clearDebugIdentity()

  if (!isWeChatBrowser()) {
    store.commit('webIdentity/SET_GUEST')
    return { status: 'guest' }
  }

  const currentUrl = new URL(window.location.href)
  const code = currentUrl.searchParams.get('code')
  const state = currentUrl.searchParams.get('state')
  const token = window.localStorage.getItem(WEB_IDENTITY_TOKEN_KEY)

  if (code) {
    const expectedState = window.sessionStorage.getItem(OAUTH_STATE_KEY)
    if (!expectedState || !state || expectedState !== state) {
      return retryAuthorization('微信授权状态校验失败，请重新进入页面')
    }
    const result = await http.postParams(baseURL.weixin.webLogin(), { code }, 1)
    if (!result || result.code !== 200 || !result.data || !result.data.token) {
      return retryAuthorization((result && result.message) || '微信授权登录失败，请重新进入页面')
    }
    replaceWithCleanUrl()
    window.sessionStorage.removeItem(OAUTH_STATE_KEY)
    window.sessionStorage.removeItem(OAUTH_RETRY_KEY)
    return setAuthenticated(result.data)
  }

  if (token) {
    const result = await http.get(baseURL.weixin.webSession(), 1)
    if (result && result.code === 200 && result.data && result.data.user) {
      window.sessionStorage.removeItem(OAUTH_RETRY_KEY)
      return setAuthenticated(result.data)
    }
    window.localStorage.removeItem(WEB_IDENTITY_TOKEN_KEY)
  }

  window.sessionStorage.removeItem(OAUTH_RETRY_KEY)
  return startAuthorization()
}

export function ensureWebIdentity() {
  if (!initializingPromise) {
    initializingPromise = initialize().catch(error => {
      const message = error && error.message ? error.message : '微信身份初始化失败'
      store.commit('webIdentity/SET_ERROR', message)
      return { status: 'error', message }
    })
  }
  return initializingPromise
}
